/**
 * Notifications Service
 * Handles all notification-related data operations with workspace-first RLS
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database, Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';
import { getSupabaseErrorMessage } from '@/lib/errorHandler';
import { WorkspaceScope, applyWorkspaceScope } from '@/lib/workspaceScope';

export type Notification = Tables<'notifications'>;

export interface CreateNotificationInput extends Omit<TablesInsert<'notifications'>, 'workspace_id' | 'user_id'> {
  workspace_id: string;
  user_id: string;
}

export interface UpdateNotificationInput extends Partial<TablesUpdate<'notifications'>> {
  id: string;
  workspace_id: string;
}

export class NotificationsService {
  /**
   * Get all notifications for a user in a workspace scope
   */
  static async getNotifications(
    scope: WorkspaceScope,
    userId: string,
    filters?: {
      isRead?: boolean;
      scheduledForFrom?: string;
      scheduledForTo?: string;
      type?: string;
    }
  ): Promise<Notification[]> {
    let query = supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId);

    // Apply workspace scope filter
    query = applyWorkspaceScope(query, scope);

    if (filters?.isRead !== undefined) {
      query = query.eq('is_read', filters.isRead);
    }

    if (filters?.scheduledForFrom) {
      query = query.gte('scheduled_for', filters.scheduledForFrom);
    }

    if (filters?.scheduledForTo) {
      query = query.lte('scheduled_for', filters.scheduledForTo);
    }

    if (filters?.type) {
      query = query.eq('type', filters.type);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      logger.error('[NotificationsService] Error fetching notifications', error, { scope, userId, filters });
      
      if (error.code === '42501' || error.code === 'PGRST301' || 
          error.message?.includes('permission') || error.message?.includes('RLS')) {
        logger.warn('[NotificationsService] Permission denied, returning empty array', { scope, userId });
        return [];
      }
      
      throw new Error(getSupabaseErrorMessage(error));
    }

    return (data || []) as Notification[];
  }

  /**
   * Create a notification
   */
  static async createNotification(input: CreateNotificationInput): Promise<Notification> {
    logger.info('[NotificationsService.createNotification] Starting', { 
      workspaceId: input.workspace_id, 
      userId: input.user_id,
      type: input.type 
    });
    
    const { data, error } = await supabase
      .from('notifications')
      .insert(input as any)
      .select()
      .single();

    if (error) {
      logger.error('[NotificationsService] Error creating notification', error, { 
        workspaceId: input.workspace_id,
        userId: input.user_id,
        errorCode: error.code,
        errorMessage: error.message
      });
      throw new Error(getSupabaseErrorMessage(error));
    }

    logger.info('[NotificationsService.createNotification] Success', { 
      notificationId: data?.id, 
      workspaceId: input.workspace_id 
    });
    return data as Notification;
  }

  /**
   * Mark notification as read
   */
  static async markAsRead(input: UpdateNotificationInput): Promise<Notification> {
    const { id, workspace_id, ...updates } = input;

    logger.info('[NotificationsService.markAsRead] Starting', { 
      notificationId: id, 
      workspaceId: workspace_id
    });

    const { data, error } = await supabase
      .from('notifications')
      .update({ 
        is_read: true,
        read_at: new Date().toISOString(),
        ...updates 
      } as any)
      .eq('id', id)
      .eq('workspace_id', workspace_id)
      .select()
      .single();

    if (error) {
      logger.error('[NotificationsService] Error marking notification as read', error, { 
        notificationId: id, 
        workspaceId: workspace_id
      });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return data as Notification;
  }

  /**
   * Delete notification
   */
  static async deleteNotification(notificationId: string, workspaceId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
      .eq('workspace_id', workspaceId);

    if (error) {
      logger.error('[NotificationsService] Error deleting notification', error, { notificationId, workspaceId });
      throw new Error(getSupabaseErrorMessage(error));
    }
  }

  /**
   * Delete notifications by deal_id (when deal is won/lost or expected_close_date is removed)
   */
  static async deleteNotificationsByDeal(dealId: string, workspaceId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('deal_id', dealId)
      .eq('workspace_id', workspaceId);

    if (error) {
      logger.error('[NotificationsService] Error deleting notifications by deal', error, { dealId, workspaceId });
      throw new Error(getSupabaseErrorMessage(error));
    }
  }
}
